import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileDown, ArrowUpRight, ShieldCheck, Sparkles, GraduationCap, Building2, Sprout, Layers, Landmark, Lock } from "lucide-react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import { setIntent } from "../lib/nav";
import { FAQS } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// CSR-related questions from the shared FAQ store (route CSR spend, track money, sponsor a cohort).
const CSR_FAQ_INDICES = [0, 1, 3];

const SCHEDULE_TAGS = [
    "Schedule VII (ii) · Employability",
    "Schedule VII (x) · Rural development",
    "Skill India / NSDC alignment",
    "Digital India · BharatNet",
    "Audit-ready impact reporting",
    "Founder-reviewed, no middlemen",
];

// What CSR / grant funding actually buys. Soft starting bands only - final
// scope is shaped in conversation. The SME pilot is the Schedule VII-safe
// field/training/impact layer; product engineering is never funded here.
const ENGAGEMENT = [
    { id: "cohort", icon: GraduationCap, name: "Learner Cohort Sponsorship", band: "from ~₹5L", text: "Fund 15-30 verified rural learners through a full talent track. Named recognition, quarterly progress reports, and first option to hire top performers as paid interns.", cta: "Sponsor a cohort", intent: "impact-funder" },
    { id: "hub", icon: Building2, name: "Regional Training Hub", band: "from ~₹25L", text: "Equip a Loni-style hub in an underserved district: hardware, mentorship and a 12-month curriculum delivery for a new pipeline of local talent.", cta: "Back a hub", intent: "impact-funder" },
    { id: "sme-pilot", icon: Sprout, name: "Rural SME Digital-Enablement Pilot", band: "scoped per district", text: "Bring digital tools and AI-assisted workflows to underserved rural and semi-urban SMEs, with on-ground training, onboarding and measured livelihood outcomes.", metric: "Digitisation & livelihood outcomes measured and reported per district.", cta: "Scope a pilot", intent: "sme-pilot" },
    { id: "multiyear", icon: Layers, name: "Multi-year Programme", band: "₹1Cr+", text: "A multi-year CSR contract under the Delivery Alliance MoU: cohorts, hub and pilot at scale, with board-ready quarterly reporting and live dashboard access for funder and auditor.", cta: "Plan a programme", intent: "impact-funder" },
];

const CsrPage = () => (
    <PageLayout title="CSR Activities · IndoBlitz" testId="csr-page">
        <PageHero
            eyebrow="CSR Activities"
            title="Real delivery."
            accent="Audit-ready impact."
            intro="Route Section 135 CSR spend into measurable rural skilling and digital enablement - through a clean, two-agreement structure that keeps your disbursement compliant and your impact transparent."
            testId="csr-hero"
        />

        {/* CSR briefing PDF download */}
        <section className="relative pb-4">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <a
                    href={`${BACKEND_URL}/api/csr-briefing.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block rounded-2xl overflow-hidden"
                    style={{
                        background: "linear-gradient(135deg, rgba(212,168,87,0.10) 0%, rgba(20,20,26,0.7) 60%, rgba(15,15,20,0.9) 100%)",
                        border: "1px solid rgba(212,168,87,0.28)",
                        boxShadow: "0 20px 50px -30px rgba(212,168,87,0.25)",
                    }}
                    data-testid="csr-briefing-download"
                >
                    <div className="relative p-5 lg:p-6 flex items-center gap-5">
                        <div
                            className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 relative overflow-hidden"
                            style={{ background: "linear-gradient(180deg, #1A1A22, #0A0A0C)", border: "1px solid rgba(212,168,87,0.4)" }}
                        >
                            <FileDown className="w-6 h-6 text-gold relative z-10" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-gold/85 mb-1">For CSR heads · Finance · Board review</div>
                            <div className="font-display text-lg lg:text-xl text-ivory leading-tight">
                                Download the <span className="italic text-gold-shimmer">CSR briefing</span> (PDF, 1 page)
                            </div>
                            <div className="mt-1.5 text-[12px] text-ivory/55 leading-snug">
                                Tripartite structure · Section 135 mapping · audit-ready · engagement tiers. Forward internally to your finance &amp; legal teams.
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gold shrink-0 group-hover:gap-2.5 transition-all">
                            Download <ArrowUpRight className="w-4 h-4" />
                        </div>
                    </div>
                </a>
            </div>
        </section>

        {/* What CSR & grants fund */}
        <section className="relative py-10" data-testid="csr-programs">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <div className="pill"><span className="accent-dot" />What your funding backs</div>
                <h2 className="mt-4 font-display text-3xl lg:text-4xl text-ivory tracking-tight">
                    Fund outcomes, <span className="italic text-gold-shimmer">not overhead.</span>
                </h2>
                <p className="mt-3 text-ivory/65 max-w-2xl leading-relaxed text-sm">
                    Every rupee buys measurable rural delivery - trained learners, equipped hubs and digitised SMEs - routed through the audit-ready, two-agreement structure above. Soft starting bands below; final scope is shaped in one conversation.
                </p>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    {ENGAGEMENT.map((p) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5 }}
                                className="glass glass-hover rounded-2xl p-6 flex flex-col"
                                data-testid={`csr-program-${p.id}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-11 h-11 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-gold" />
                                    </div>
                                    <span className="font-display text-lg text-gold-shimmer">{p.band}</span>
                                </div>
                                <h3 className="mt-5 font-display text-xl text-ivory leading-tight">{p.name}</h3>
                                <p className="mt-2 text-sm text-ivory/60 leading-relaxed flex-1">{p.text}</p>
                                {p.metric && (
                                    <div className="mt-3 flex items-start gap-2 text-[12px] text-teal/90 leading-snug" data-testid={`csr-program-metric-${p.id}`}>
                                        <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                        <span>{p.metric}</span>
                                    </div>
                                )}
                                <Link
                                    to="/contact"
                                    onClick={() => setIntent(p.intent)}
                                    className="btn-ghost mt-5 !py-2.5 text-sm w-full justify-center"
                                    data-testid={`csr-program-cta-${p.id}`}
                                >
                                    {p.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Grant-friendly line */}
                <div
                    className="mt-5 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                    style={{ background: "linear-gradient(135deg, rgba(45,212,191,0.06), rgba(20,20,26,0.6))", border: "1px solid rgba(45,212,191,0.22)" }}
                    data-testid="csr-grant-note"
                >
                    <div className="w-11 h-11 rounded-xl bg-teal/10 border border-teal/25 flex items-center justify-center shrink-0">
                        <Landmark className="w-5 h-5 text-teal" />
                    </div>
                    <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-teal/90 mb-1">Government · Multilateral · Foundation grants</div>
                        <p className="text-sm text-ivory/70 leading-relaxed">Our work maps to Skill India, Digital India and BharatNet missions. We provide the baselines, documentation and reporting cadence grant committees expect.</p>
                    </div>
                    <Link to="/contact" onClick={() => setIntent("impact-funder")} className="btn-ghost !py-2.5 text-sm shrink-0" data-testid="csr-grant-cta">
                        Discuss a grant <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        </section>

        {/* Schedule VII alignment */}
        <section className="relative py-10">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6 }}
                    className="rounded-2xl p-7 lg:p-10"
                    style={{
                        background: "linear-gradient(120deg, rgba(212,168,87,0.10) 0%, rgba(45,212,191,0.08) 50%, rgba(20,20,26,0.7) 100%)",
                        border: "1px solid rgba(212,168,87,0.28)",
                    }}
                    data-testid="csr-schedule-vii"
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/10 text-gold text-[10px] font-mono uppercase tracking-widest">
                        <ShieldCheck className="w-3 h-3" /> Section 135 · Schedule VII aligned
                    </div>
                    <h2 className="mt-5 font-display text-3xl lg:text-4xl text-ivory tracking-tight leading-[1.05]">
                        Build the movement <span className="italic text-gold-shimmer">with us.</span>
                    </h2>
                    <p className="mt-4 text-ivory/75 leading-relaxed max-w-3xl">
                        IndoBlitz trains rural India's young talent for global delivery, mapped to Schedule VII (ii) employability
                        and (x) rural development categories, and aligned to Skill India, Digital India and BharatNet missions.
                        We invite Section 135 CSR partners, family foundations and government agencies to co-build measurable rural
                        outcomes - with reporting, governance and audit cadence built in.
                    </p>
                    <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
                        {SCHEDULE_TAGS.map((tag) => (
                            <li key={tag} className="flex items-start gap-2 text-sm text-ivory/75">
                                <Sparkles className="w-3.5 h-3.5 text-gold mt-1 shrink-0" />
                                <span>{tag}</span>
                            </li>
                        ))}
                    </ul>
                </motion.div>
            </div>
        </section>

        {/* CSR FAQs */}
        <section className="relative py-6 lg:py-10">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <div className="pill"><span className="accent-dot" />CSR questions</div>
                <h2 className="mt-4 font-display text-3xl lg:text-4xl text-ivory tracking-tight">
                    How CSR works <span className="italic text-gold-shimmer">with us.</span>
                </h2>
                <div className="mt-6 space-y-3">
                    {CSR_FAQ_INDICES.map((idx) => (
                        <div key={idx} className="glass rounded-2xl p-6" data-testid={`csr-faq-${idx}`}>
                            <h3 className="font-display text-lg text-gold leading-snug">{FAQS[idx].q}</h3>
                            <p className="mt-2 text-ivory/70 text-sm leading-relaxed">{FAQS[idx].a}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/community-impact" className="btn-ghost" data-testid="csr-field-dispatches">
                        See field dispatches <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link to="/contact" onClick={() => setIntent("impact-funder")} className="btn-gold" data-testid="csr-contact">
                        Open a partnership conversation <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
        {/* Strategic & investor partnerships - discreet, NDA-gated, no figures */}
        <section className="relative py-10" data-testid="csr-strategic">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <div
                    className="rounded-2xl p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
                    style={{ background: "linear-gradient(135deg, rgba(212,168,87,0.05), rgba(20,20,26,0.7))", border: "1px solid rgba(212,168,87,0.18)" }}
                >
                    <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0">
                        <Lock className="w-5 h-5 text-gold" />
                    </div>
                    <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-gold/85 mb-1">Strategic & investor partnerships</div>
                        <h3 className="font-display text-xl lg:text-2xl text-ivory leading-tight">Building something bigger.</h3>
                        <p className="mt-2 text-sm text-ivory/65 leading-relaxed max-w-2xl">
                            We develop serious in-house technology platforms alongside our impact work. Strategic and investor partnerships are discussed privately, under NDA - we prefer execution and proof before we share.
                        </p>
                    </div>
                    <Link to="/contact" onClick={() => setIntent("strategic-partnership")} className="btn-ghost !py-2.5 text-sm shrink-0" data-testid="csr-strategic-cta">
                        Request a private conversation <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        </section>
    </PageLayout>
);

export default CsrPage;
